import type {
    CollisionDetection,
    DragEndEvent,
    DragMoveEvent,
    DragStartEvent,
} from '@dnd-kit/core';
import { pointerWithin, rectIntersection } from '@dnd-kit/core';
import { stripMoneyMapAccountFromRules } from '@fruitful/money-map';
import {
    type DeepPartial,
    type MoneyMapRuleUpsertDto,
    type MoneyMapRuleWithAccountDetails,
    type MoneyMapWithRulesAndAccounts,
    type RuleOrGroup,
} from '@fruitful/types';
import { sortBy } from 'lodash';

import { BACKDOOR_ROTH_ENABLED_IN_GROVE } from '../../../config/FruitfulEnvironment';
import type { MembershipDetails } from '../../../entities/entityTypes';
import {
    buildGoalPanelPresetGroupDto,
    GoalPanelPreset,
    isSingleGoalPanelPreset,
} from '../../utils/buildGoalPanelPresetGroupDto';
import type { DropIndicator } from './goalsWaterfallTypes';
import { isPresetMergeDropIndicator } from './goalsWaterfallTypes';

export type GoalPanelPresetDropOutcome = 'merged' | 'inserted' | 'cancelled';

type PresetDragEvent =
    | Pick<DragStartEvent, 'active'>
    | Pick<DragMoveEvent, 'active'>
    | Pick<DragEndEvent, 'active'>;

/** Reads preset from dnd-kit active drag data. */
export const getPresetFromDragEvent = (
    event: PresetDragEvent,
): GoalPanelPreset | undefined =>
    event.active.data.current?.preset as GoalPanelPreset | undefined;

/** Pointer Y → top-level waterfall insert index (between `[data-goal-item-id]` rows). */
export const computeWaterfallInsertIndexFromPointer = (
    pointerY: number,
    fallbackLength: number,
): number => {
    const goalItemElements = Array.from(
        document.querySelectorAll('[data-goal-item-id]'),
    );
    for (let i = 0; i < goalItemElements.length; i++) {
        const rect = goalItemElements[i]?.getBoundingClientRect();
        if (rect != null && pointerY < rect.top + rect.height / 2) {
            return i;
        }
    }
    return fallbackLength;
};

/** Prefer goal targets for combine; fall back to the open drop zone for insert. */
export const createGoalPanelPresetCollisionDetection = (
    waterfallCollisionDetection: CollisionDetection,
): CollisionDetection => {
    return (args) => {
        if (args.active.data.current?.preset == null) {
            return waterfallCollisionDetection(args);
        }

        const waterfallHits = waterfallCollisionDetection(args);
        const goalHit = waterfallHits.find(
            (c) => c.id !== 'goals-waterfall-drop-zone',
        );
        if (goalHit != null) {
            return [goalHit];
        }

        const pointerHits = pointerWithin(args);
        const rectHits = rectIntersection(args);
        const byId = new Map(
            [...pointerHits, ...rectHits].map((c) => [c.id, c] as const),
        );
        const hits = [...byId.values()];
        const dropZoneHit = hits.find(
            (c) => c.id === 'goals-waterfall-drop-zone',
        );
        if (dropZoneHit != null) {
            return [dropZoneHit];
        }
        return hits;
    };
};

const mergeGoalPanelPresetIntoGroup = async ({
    preset,
    membership,
    moneyMapId,
    fetchedMoneyMap,
    targetGroupId,
    monthlyExpensesCents,
    insertIndexInGroup,
    getGroupRules,
    onUpdateMoneyMap,
}: {
    preset: GoalPanelPreset;
    membership: MembershipDetails;
    moneyMapId: string;
    fetchedMoneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    targetGroupId: string;
    monthlyExpensesCents: number;
    insertIndexInGroup: number | null;
    getGroupRules: (
        groupId: string | undefined,
    ) => Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
}): Promise<boolean> => {
    const targetGroup = (fetchedMoneyMap.moneyMapRuleGroups ?? []).find(
        (g) => g.id === targetGroupId,
    );
    if (targetGroup?.id == null) {
        return false;
    }

    const presetGroupDto = buildGoalPanelPresetGroupDto({
        preset,
        membership,
        moneyMapId,
        priority: targetGroup.priority ?? 1,
        monthlyExpensesCents,
    });

    const incomingRules: MoneyMapRuleUpsertDto[] = (
        presetGroupDto.moneyMapRules ?? []
    ).map((rule) => ({
        ...rule,
        moneyMapRuleGroupId: targetGroupId,
    }));

    // Mirror the standard money map editor's save (`GoalForm.onSubmit` in
    // components/forms/GoalForm.tsx): existing rules are sent with their hydrated
    // `moneyMapAccount` stripped (via `stripMoneyMapAccountFromRules`) so the backend
    // leaves those accounts untouched, while only the new/changed rule carries a full
    // account. Here the new rules are `incomingRules`, which we must NOT strip — they
    // hold the preset's proposed account (incl. the nested backdoor Roth). Re-sending
    // existing accounts as full upserts would make the backend re-create/re-link them,
    // racing with the new account and dropping the backdoor Roth link.
    const existingGroupRules = stripMoneyMapAccountFromRules(
        sortBy(getGroupRules(targetGroupId), 'priority'),
    );

    const combinedRules: Array<
        DeepPartial<MoneyMapRuleWithAccountDetails> | MoneyMapRuleUpsertDto
    > =
        insertIndexInGroup != null
            ? [
                  ...existingGroupRules.slice(0, insertIndexInGroup),
                  ...incomingRules,
                  ...existingGroupRules.slice(insertIndexInGroup),
              ]
            : [...existingGroupRules, ...incomingRules];

    const withPriorities = combinedRules.map((rule, index) => ({
        ...rule,
        moneyMapRuleGroupId: targetGroupId,
        priority: index + 1,
    }));

    const allRules = fetchedMoneyMap.moneyMapRules ?? [];
    const rulesOutsideTargetGroup = stripMoneyMapAccountFromRules(
        allRules.filter((rule) => rule.moneyMapRuleGroupId !== targetGroupId),
    );

    return await onUpdateMoneyMap({
        moneyMapRules: [...rulesOutsideTargetGroup, ...withPriorities],
    });
};

const insertGoalPanelPresetAtTopLevel = async ({
    preset,
    membership,
    moneyMapId,
    fetchedMoneyMap,
    goalItems,
    nonGoalRuleCount,
    insertIndex,
    monthlyExpensesCents,
    onUpdateMoneyMap,
}: {
    preset: GoalPanelPreset;
    membership: MembershipDetails;
    moneyMapId: string;
    fetchedMoneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    goalItems: RuleOrGroup[];
    nonGoalRuleCount: number;
    insertIndex: number;
    monthlyExpensesCents: number;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
}): Promise<boolean> => {
    const insertPriority =
        goalItems[insertIndex]?.priority ??
        (goalItems[goalItems.length - 1]?.priority ?? nonGoalRuleCount) + 1;

    const newGroup = buildGoalPanelPresetGroupDto({
        preset,
        membership,
        moneyMapId,
        priority: insertPriority,
        monthlyExpensesCents,
    });

    const updatedExistingGroups = (
        fetchedMoneyMap.moneyMapRuleGroups ?? []
    ).map((g) => ({
        ...g,
        priority:
            (g.priority ?? 0) >= insertPriority
                ? (g.priority ?? 0) + 1
                : g.priority,
    }));

    return await onUpdateMoneyMap({
        // Mirror the standard money map editor's save (`GoalForm.onSubmit` in
        // components/forms/GoalForm.tsx): existing rules are sent with their hydrated
        // `moneyMapAccount` stripped so the backend doesn't re-upsert/re-link those
        // accounts. The net-new preset rule keeps its full account (incl. the nested
        // backdoor Roth) inside `newGroup.moneyMapRules`. Without stripping, re-sending
        // existing accounts races with creating the new one and drops the Roth link.
        moneyMapRules: stripMoneyMapAccountFromRules(
            fetchedMoneyMap.moneyMapRules ?? [],
        ),
        moneyMapRuleGroups: [...updatedExistingGroups, newGroup],
    });
};

interface HandleGoalPanelPresetDropParams {
    event: DragEndEvent;
    dropIndicator: DropIndicator;
    previewInsertIndex: number | null;
    dragStartPointerY: number;
    membership: MembershipDetails | null;
    fetchedMoneyMap:
        | DeepPartial<MoneyMapWithRulesAndAccounts>
        | null
        | undefined;
    goalItems: RuleOrGroup[];
    nonGoalRuleCount: number;
    monthlyExpensesCents: number;
    getGroupRules: (
        groupId: string | undefined,
    ) => Array<DeepPartial<MoneyMapRuleWithAccountDetails>>;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
}

/**
 * Resolves a preset panel drop as merge-into-group, insert-as-new-set, or cancel.
 * Merge vs insert is driven by the same drop indicators as in-waterfall goal combining.
 */
export const handleGoalPanelPresetDrop = async ({
    event,
    dropIndicator,
    previewInsertIndex,
    dragStartPointerY,
    membership,
    fetchedMoneyMap,
    goalItems,
    nonGoalRuleCount,
    monthlyExpensesCents,
    getGroupRules,
    onUpdateMoneyMap,
}: HandleGoalPanelPresetDropParams): Promise<GoalPanelPresetDropOutcome> => {
    const preset = getPresetFromDragEvent(event);
    if (preset == null || membership == null || fetchedMoneyMap?.id == null) {
        return 'cancelled';
    }

    if (
        (preset === GoalPanelPreset.BACKDOOR_ROTH ||
            preset === GoalPanelPreset.BACKDOOR_ROTH_PARTNER) &&
        !BACKDOOR_ROTH_ENABLED_IN_GROVE
    ) {
        return 'cancelled';
    }

    const effectiveOverId =
        event.over?.id?.toString() ?? dropIndicator?.targetId;
    const groups = fetchedMoneyMap.moneyMapRuleGroups ?? [];
    const allRules = fetchedMoneyMap.moneyMapRules ?? [];
    const overGroup =
        effectiveOverId != null
            ? groups.find((g) => g.id === effectiveOverId)
            : undefined;
    const overGroupGoalRule =
        effectiveOverId != null
            ? allRules.find(
                  (r) =>
                      r.id === effectiveOverId && r.moneyMapRuleGroupId != null,
              )
            : undefined;

    if (
        isSingleGoalPanelPreset(preset) &&
        overGroup != null &&
        dropIndicator?.kind === 'group-outline'
    ) {
        await mergeGoalPanelPresetIntoGroup({
            preset,
            membership,
            moneyMapId: fetchedMoneyMap.id,
            fetchedMoneyMap,
            targetGroupId: overGroup.id ?? '',
            monthlyExpensesCents,
            insertIndexInGroup: null,
            getGroupRules,
            onUpdateMoneyMap,
        });
        return 'merged';
    }

    if (
        overGroupGoalRule?.moneyMapRuleGroupId != null &&
        effectiveOverId != null &&
        dropIndicator?.groupId != null &&
        (dropIndicator.kind === 'line-before' ||
            dropIndicator.kind === 'line-after')
    ) {
        const targetGroupRules = getGroupRules(
            overGroupGoalRule.moneyMapRuleGroupId,
        ).sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
        const overIndex = targetGroupRules.findIndex(
            (r) => r.id === effectiveOverId,
        );
        if (overIndex !== -1) {
            const insertIndexInGroup =
                dropIndicator.kind === 'line-before'
                    ? overIndex
                    : overIndex + 1;
            await mergeGoalPanelPresetIntoGroup({
                preset,
                membership,
                moneyMapId: fetchedMoneyMap.id,
                fetchedMoneyMap,
                targetGroupId: overGroupGoalRule.moneyMapRuleGroupId,
                monthlyExpensesCents,
                insertIndexInGroup,
                getGroupRules,
                onUpdateMoneyMap,
            });
            return 'merged';
        }
    }

    const pointerY = dragStartPointerY + event.delta.y;
    const insertIndex =
        previewInsertIndex ??
        computeWaterfallInsertIndexFromPointer(pointerY, goalItems.length);

    if (
        previewInsertIndex == null &&
        event.over?.id !== 'goals-waterfall-drop-zone'
    ) {
        return 'cancelled';
    }

    await insertGoalPanelPresetAtTopLevel({
        preset,
        membership,
        moneyMapId: fetchedMoneyMap.id,
        fetchedMoneyMap,
        goalItems,
        nonGoalRuleCount,
        insertIndex,
        monthlyExpensesCents,
        onUpdateMoneyMap,
    });
    return 'inserted';
};

/** Updates insert-preview index during preset drag (null when merging). */
export const computeGoalPanelPresetPreviewInsertIndex = ({
    event,
    dropIndicator,
    goalItemCount,
}: {
    event: { activatorEvent: Event; delta: { y: number } };
    dropIndicator: DropIndicator;
    goalItemCount: number;
}): number | null => {
    if (isPresetMergeDropIndicator(dropIndicator)) {
        return null;
    }

    if (!(event.activatorEvent instanceof PointerEvent)) {
        return null;
    }

    const pointerY = event.activatorEvent.clientY + event.delta.y;
    return computeWaterfallInsertIndexFromPointer(pointerY, goalItemCount);
};
